import { useState, useEffect } from 'react';
import { usePersonalDna, DnaNotices, DnaSaveBar } from '../../components/projects/PersonalDnaProfile';
import PersonalDnaOverview from '../../components/projects/PersonalDnaOverview';
import PersonalCvUpdate from '../../components/projects/PersonalCvUpdate';
import { DnaProfileTab, DnaCareerTab } from '../../components/projects/PersonalProfileEditor';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BrandDnaChat from './BrandDnaChat';
import LogoPlacementEditor from '../../components/LogoPlacementEditor';
import BrandMediaManager from '../../components/BrandMediaManager';
import BrandDNAUebersicht from '../../components/BrandDnaDimensionsEditor';
import BrandDnaAdvancedSettings from '../../components/BrandDnaAdvancedSettings';
import BrandDnaPreferences from '../../components/BrandDnaPreferences';
import BrandCompanyEditor from '../../components/BrandCompanyEditor';
import BrandLegacyVisualEditor from '../../components/BrandLegacyVisualEditor';
import BrandManualImport from '../../components/BrandManualImport';
import BrandDnaReanalyze from '../../components/BrandDnaReanalyze';

// Read-only "Brand DNA" overview. Shows the customer the CURRENT state of
// their brand DNA across three blocks. Purely reading — this view never
// writes, renames, or migrates any field. Data comes from two existing
// GET endpoints:
//   GET /api/settings/brand-dna   -> data.brandDNA (structured) + data.company.industry
//   GET /api/brand/inspirations   -> data.inspirations[] (example posts)

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// ── xchannels light palette ──────────────────────────────────────────────
// Dark app palette (from Settings.js / DashboardLayout — not invented):
// page #0A1628 · card #1A2B42 · accent #00D9C8 · text white · muted #A0AEC0.
// `page` doubles as the inner-element background (darker than the card).
const C = {
  page:   '#0A1628',
  card:   '#1A2B42',
  border: '#2A3B52',
  text:   '#FFFFFF',
  accent: '#00D9C8',
  muted:  '#A0AEC0',
};

const font = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

const styles = {
  page: {
    minHeight: '100%',
    background: C.page,
    color: C.text,
    fontFamily: font,
    padding: '32px 24px 64px',
  },
  wrap: { maxWidth: 880, margin: '0 auto' },
  h1: { fontSize: 26, fontWeight: 700, margin: '0 0 4px' },
  pageSub: { fontSize: 14, color: C.muted, margin: '0 0 28px' },
  card: {
    background: C.card,
    border: `0.5px solid ${C.border}`,
    borderRadius: 12,
    padding: '22px 24px',
    marginBottom: 20,
  },
  blockTitle: { fontSize: 18, fontWeight: 700, margin: 0 },
  blockSub: { fontSize: 12.5, color: C.muted, margin: '2px 0 0' },
  blockHead: { marginBottom: 18 },
  fieldLabel: { fontSize: 12, fontWeight: 600, letterSpacing: '.02em', margin: 0 },
  fieldValue: { fontSize: 14.5, lineHeight: 1.5, margin: '5px 0 0' },
  fieldWrap: { marginBottom: 16 },
  empty: { fontSize: 13.5, color: C.muted, fontStyle: 'italic', margin: '5px 0 0' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 7 },
  chip: {
    fontSize: 12.5, fontWeight: 500,
    padding: '5px 11px', borderRadius: 999,
    border: `0.5px solid ${C.border}`, background: C.page, color: C.text,
  },
  personaGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 12, marginTop: 8,
  },
  persona: {
    border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '13px 15px',
    background: C.page,
  },
  personaName: { fontSize: 14, fontWeight: 700, margin: 0 },
  personaLine: { fontSize: 12.5, color: C.text, margin: '6px 0 0', lineHeight: 1.45 },
  personaKey: { color: C.muted, fontWeight: 600 },
  primaryTag: {
    fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
    color: C.accent, border: `0.5px solid ${C.accent}`,
    borderRadius: 999, padding: '2px 8px', marginLeft: 8,
  },
  postRow: {
    border: `0.5px solid ${C.border}`, borderRadius: 10, padding: '12px 14px',
    background: C.page, marginTop: 10,
  },
  postPlatform: { fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '.03em' },
  pill: (active) => ({ fontSize: 12, fontWeight: 600, padding: '4px 11px', borderRadius: 999, cursor: 'pointer', border: `0.5px solid ${active ? C.accent : C.border}`, background: active ? 'rgba(0,217,200,.15)' : C.page, color: active ? C.text : C.muted }),
  postText: { fontSize: 13, lineHeight: 1.5, color: C.text, margin: '6px 0 0', whiteSpace: 'pre-wrap' },
  badgeWarn: { fontSize: 10.5, fontWeight: 700, color: '#F5A524', border: '0.5px solid #F5A524', borderRadius: 999, padding: '1px 8px', marginLeft: 8, cursor: 'help' },
  badgeOwn: { fontSize: 10.5, fontWeight: 700, color: C.accent, border: `0.5px solid ${C.accent}`, borderRadius: 999, padding: '1px 8px', marginLeft: 8 },
  badgeOther: { fontSize: 10.5, fontWeight: 700, color: C.muted, border: `0.5px solid ${C.border}`, borderRadius: 999, padding: '1px 8px', marginLeft: 8 },
  editBox: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 },
  editTextarea: { fontSize: 13.5, lineHeight: 1.5, fontFamily: font, padding: '9px 11px', borderRadius: 8, border: `0.5px solid ${C.border}`, background: C.page, color: C.text, outline: 'none', minHeight: 200, resize: 'vertical' },
  warnText: { fontSize: 12, color: '#F5A524', margin: '8px 0 0' },
  countPill: { fontSize: 12, color: C.muted, marginTop: 4 },
  swatchRow: { display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 },
  swatch: { textAlign: 'center', width: 84 },
  swatchBox: { width: 84, height: 56, borderRadius: 8, border: `0.5px solid ${C.border}` },
  swatchHex: { fontSize: 11.5, fontWeight: 600, marginTop: 5, fontFamily: 'monospace' },
  swatchRole: { fontSize: 10.5, color: C.muted, marginTop: 1 },
  logoBox: {
    width: 120, height: 120, borderRadius: 10, border: `0.5px solid ${C.border}`,
    background: C.page, display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', marginTop: 8,
  },
  logoImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  logoPlaceholder: { fontSize: 12, color: C.muted, textAlign: 'center', padding: 10 },
  // interactive controls (1b/1c/1d)
  btn: { fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 8, cursor: 'pointer', border: `0.5px solid ${C.border}`, background: C.page, color: C.text },
  btnPrimary: { fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 8, cursor: 'pointer', border: `0.5px solid ${C.accent}`, background: C.accent, color: '#0A1628' },
  editInput: { fontSize: 14, fontFamily: font, padding: '7px 10px', borderRadius: 8, border: `0.5px solid ${C.border}`, background: C.page, color: C.text, outline: 'none', minWidth: 220 },
  inlineRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 5, flexWrap: 'wrap' },
  linkBtn: { fontSize: 12, color: C.accent, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 8 },
  hint: { fontSize: 12, color: C.muted, fontStyle: 'italic', margin: '8px 0 0' },
  sugRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 },
  roleSelect: { fontSize: 12, padding: '4px 6px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.page, color: C.text },
  removeX: { fontSize: 12, color: C.muted, background: 'none', border: 'none', cursor: 'pointer' },
  uploadLabel: { display: 'inline-block', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 8, cursor: 'pointer', border: `0.5px solid ${C.border}`, background: C.page, color: C.text, marginTop: 8 },
  // add-post form (Fix 2)
  postHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  addPostBox: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  postTextarea: { fontSize: 13.5, fontFamily: font, padding: '9px 11px', borderRadius: 8, border: `0.5px solid ${C.border}`, background: C.page, color: C.text, outline: 'none', minHeight: 64, resize: 'vertical' },
  // style fidelity + overlay (ported)
  segRow: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  seg: (active) => ({ fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer', border: `0.5px solid ${active ? C.accent : C.border}`, background: active ? C.accent : C.page, color: active ? '#0A1628' : C.text }),
  toggleRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 },
  toggle: (on) => ({ width: 42, height: 24, borderRadius: 999, border: `0.5px solid ${C.border}`, background: on ? C.accent : C.page, cursor: 'pointer', position: 'relative', transition: 'background .15s' }),
  toggleKnob: (on) => ({ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: 999, background: '#fff', border: `0.5px solid ${C.border}`, transition: 'left .15s' }),
  advancedToggle: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'none', border: 'none', cursor: 'pointer', color: C.text, padding: 0, textAlign: 'left' },
  advancedHint: { fontSize: 12, color: C.muted, fontWeight: 400 },
};

// ── Localized strings — follow the app language toggle (single language) ──
const L = {
  de: {
    pageTitle: 'Brand DNA', pageSub: 'Dein aktueller Marken-Stand. Alles hier kannst du selbst anpassen – Änderungen gelten sofort für neue Inhalte.',
    pageTitleP: 'Personal DNA', pageSubP: 'Du als Marke: wer du bist, wie du klingst, wie du aussiehst. Alles hier kannst du selbst anpassen – Änderungen gelten sofort für neue Inhalte.',
    whoP: 'Wer du bist', soundP: 'Wie du klingst', lookP: 'Wie du aussiehst',
    who: 'Wer ihr seid', sound: 'Wie ihr klingt', look: 'Wie ihr ausseht',
    positioning: 'Positionierung', usp: 'Alleinstellung', industry: 'Branche',
    audiences: 'Zielgruppen', character: 'Charakter', examplePosts: 'Beispiel-Posts',
    colors: 'Farben', logo: 'Logo', referenceImages: 'Referenzbilder',
    empty: 'noch nicht erkannt', pain: 'Herausforderung', wants: 'Will',
    posts: (n) => `${n} Post${n === 1 ? '' : 's'}`,
    advanced: 'Alle Details bearbeiten', advancedHint: 'Voller 7-Dimensionen-Editor (für Fortgeschrittene)',
    personaFields: { name: 'Name / Segment', age_range: 'Alter', context: 'Situation', pain_point: 'Herausforderung', purchase_motive: 'Will / Kaufmotiv', content_hook: 'Was sie anspricht' },
    audienceRole: 'Rolle', rolePrimary: 'Primär', roleSecondary: 'Sekundär', addAudience: '+ Zielgruppe hinzufügen',
    confirmDeleteAudience: 'Diese Zielgruppe wirklich löschen?',
    productPromise: 'Produktversprechen', marketPosition: 'Marktposition', commaSeparated: 'Mit Komma trennen', onePerLine: 'Eine Vorgabe pro Zeile', extraGuidelines: 'Weitere Markenvorgaben', saved: 'Gespeichert', saveFailed: 'Speichern fehlgeschlagen',
    loading: 'Lädt…', edit: 'Bearbeiten', save: 'Speichern', cancel: 'Abbrechen',
    suggestColors: 'Farben aus Website vorschlagen',
    suggestHint: 'Automatischer Vorschlag — oft ungenau. Bitte prüfen, Rollen anpassen, dann übernehmen.',
    apply: 'Übernehmen', discard: 'Verwerfen', remove: 'Entfernen',
    uploadLogo: 'Logo hochladen', addRefImage: 'Referenzbild hinzufügen',
    uploading: 'Lädt hoch…', noSuggestions: 'Keine Farben auf der Website gefunden.',
    noUrl: 'Keine Website-URL im Profil hinterlegt — bitte zuerst eine URL speichern.',
    noColorsForUrl: (u) => `Keine Farben aus ${u || 'der Website'} lesbar (evtl. JS-Seite/SPA ohne CSS im HTML).`,
    pastePost: 'Beispiel-Post hier einfügen…', platformOptional: 'Plattform (optional)',
    addPost: 'Post hinzufügen',
    choosePlatform: 'Plattform wählen…', noPlatform: 'Keine Plattform',
    noPlatformHint: 'Ohne Plattform wird dieser Post auf LinkedIn, Instagram, Facebook und X nicht als Stilvorlage genutzt.',
    missingPlatformHint: (n) => `${n} Post${n === 1 ? ' hat' : 's haben'} keine Plattform und ${n === 1 ? 'wird' : 'werden'} nicht als Stilvorlage genutzt. Bitte zuordnen.`,
    ownBadge: 'Von mir', otherBadge: 'Vorbild',
    ownOptions: { own: 'Von mir (so schreibe ich)', other: 'Vorbild einer anderen Person', unset: 'Nicht zugeordnet' },
    ownHint: 'Eigene Posts prägen deine Stimme. Vorbilder liefern nur Anregung für Aufbau und Themen.',
    showMore: 'Mehr anzeigen', showLess: 'Weniger', showAll: (n) => `Alle ${n} anzeigen`, showFewer: 'Weniger anzeigen',
    editPost: 'Bearbeiten', whatILike: 'Was dir daran gefällt (optional)',
    suggestPlatform: (p) => `Vorschlag: ${p} – übernehmen`,
    selectedN: (n) => `${n} ausgewählt`, applyPlatform: 'Plattform setzen', markOwn: 'Von mir', markOther: 'Vorbild', clearSel: 'Auswahl aufheben',
    selectNoPlatform: 'Alle ohne Plattform auswählen', bulkFailed: 'Zuordnung fehlgeschlagen',
    styleFidelity: 'Stil-Treue', styleFidelityHint: 'Wie streng generierte Bilder dem Marken-/Referenzstil folgen.',
    fidLoose: 'Locker', fidSimilar: 'Ähnlich', fidExact: 'Genau',
    logoOverlay: 'Logo-Overlay auf Bildern', overlayOn: 'Overlay aktiv',
    overlayLogoUpload: 'Overlay-Logo hochladen', overlayPosition: 'Position', overlaySize: 'Größe',
    overlayNoLogo: 'Noch kein Overlay-Logo hochgeladen.',
  },
  en: {
    pageTitle: 'Brand DNA', pageSub: 'Your brand today. You can edit everything here — changes apply immediately to new content.',
    pageTitleP: 'Personal DNA', pageSubP: 'You as a brand: who you are, how you sound, how you look. You can edit everything here — changes apply immediately to new content.',
    whoP: 'Who you are', soundP: 'How you sound', lookP: 'How you look',
    who: 'Who you are', sound: 'How you sound', look: 'How you look',
    positioning: 'Positioning', usp: 'What sets you apart', industry: 'Industry',
    audiences: 'Audiences', character: 'Character', examplePosts: 'Example posts',
    colors: 'Colors', logo: 'Logo', referenceImages: 'Reference images',
    empty: 'not set yet', pain: 'Pain', wants: 'Wants',
    posts: (n) => `${n} post${n === 1 ? '' : 's'}`,
    advanced: 'Edit all details', advancedHint: 'Full 7-dimension editor (advanced)',
    personaFields: { name: 'Name / segment', age_range: 'Age', context: 'Situation', pain_point: 'Pain', purchase_motive: 'Wants / purchase motive', content_hook: 'What resonates' },
    audienceRole: 'Role', rolePrimary: 'Primary', roleSecondary: 'Secondary', addAudience: '+ Add audience',
    confirmDeleteAudience: 'Really delete this audience?',
    productPromise: 'Product promise', marketPosition: 'Market position', commaSeparated: 'Separate with commas', onePerLine: 'One guideline per line', extraGuidelines: 'Further brand guidelines', saved: 'Saved', saveFailed: 'Save failed',
    loading: 'Loading…', edit: 'Edit', save: 'Save', cancel: 'Cancel',
    suggestColors: 'Suggest colors from website',
    suggestHint: 'Automatic suggestion — often inaccurate. Please review, adjust roles, then apply.',
    apply: 'Apply', discard: 'Discard', remove: 'Remove',
    uploadLogo: 'Upload logo', addRefImage: 'Add reference image',
    uploading: 'Uploading…', noSuggestions: 'No colors found on the website.',
    noUrl: 'No website URL on file — please save a URL first.',
    noColorsForUrl: (u) => `Couldn't read colors from ${u || 'the website'} (likely a JS/SPA page with no CSS in the HTML).`,
    pastePost: 'Paste an example post here…', platformOptional: 'Platform (optional)',
    addPost: 'Add post',
    choosePlatform: 'Choose platform…', noPlatform: 'No platform',
    noPlatformHint: 'Without a platform this post is not used as a style reference on LinkedIn, Instagram, Facebook and X.',
    missingPlatformHint: (n) => `${n} post${n === 1 ? ' has' : 's have'} no platform and ${n === 1 ? 'is' : 'are'} not used as a style reference. Please assign one.`,
    ownBadge: 'Mine', otherBadge: 'Role model',
    ownOptions: { own: 'Mine (this is how I write)', other: 'Role model from someone else', unset: 'Not assigned' },
    ownHint: 'Your own posts shape your voice. Role models only inspire structure and topics.',
    showMore: 'Show more', showLess: 'Show less', showAll: (n) => `Show all ${n}`, showFewer: 'Show fewer',
    editPost: 'Edit', whatILike: 'What you like about it (optional)',
    suggestPlatform: (p) => `Suggestion: ${p} – apply`,
    selectedN: (n) => `${n} selected`, applyPlatform: 'Set platform', markOwn: 'Mine', markOther: 'Role model', clearSel: 'Clear selection',
    selectNoPlatform: 'Select all without platform', bulkFailed: 'Assignment failed',
    styleFidelity: 'Style fidelity', styleFidelityHint: 'How strictly generated images follow the brand/reference style.',
    fidLoose: 'Loose', fidSimilar: 'Similar', fidExact: 'Exact',
    logoOverlay: 'Logo overlay on images', overlayOn: 'Overlay enabled',
    overlayLogoUpload: 'Upload overlay logo', overlayPosition: 'Position', overlaySize: 'Size',
    overlayNoLogo: 'No overlay logo uploaded yet.',
  },
};


const ROLES = ['primary', 'secondary', 'accent', 'background'];

// Derive an audience label WITHOUT exposing a personal name. The persona name
// is stored as "Firstname L., descriptive title" — we keep only the part after
// the first comma (the segment), and fall back to other non-name fields.
const audienceLabel = (p, fallback) => {
  const name = (p?.name || '').trim();
  if (name.includes(',')) {
    const after = name.split(',').slice(1).join(',').trim();
    if (after) return after;
  }
  if (p?.context && String(p.context).trim()) return String(p.context).trim();
  if (p?.content_hook && String(p.content_hook).trim()) return String(p.content_hook).trim();
  return fallback;
};

// ── Small presentational helpers ─────────────────────────────────────────
const Field = ({ label, children, empty, emptyText }) => (
  <div style={styles.fieldWrap}>
    <p style={styles.fieldLabel}>{label}</p>
    {empty ? <p style={styles.empty}>{emptyText}</p> : <div style={styles.fieldValue}>{children}</div>}
  </div>
);

const BlockHead = ({ title }) => (
  <div style={styles.blockHead}>
    <h2 style={styles.blockTitle}>{title}</h2>
  </div>
);


// Inline-editierbares Feld: Klick auf "Bearbeiten", speichern über onSave(value).
const EditableField = ({ label, value, multiline, list, lines, onSave, empty, emptyText, t }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const display = (list || lines) ? (Array.isArray(value) ? value : []) : (value || '');
  const has = (list || lines) ? display.length > 0 : !!String(display).trim();
  const start = () => { setDraft(lines ? display.join('\n') : list ? display.join(', ') : String(display)); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      await onSave(lines ? draft.split('\n').map((x) => x.trim()).filter(Boolean) : list ? draft.split(/[,\n]/).map((x) => x.trim()).filter(Boolean) : draft.trim());
      setEditing(false);
    } finally { setSaving(false); }
  };
  const inputStyle = { width: '100%', boxSizing: 'border-box', background: C.page, color: C.text, border: `1px solid ${C.accent}55`, borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' };
  return (
    <div style={styles.fieldWrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <p style={styles.fieldLabel}>{label}</p>
        {!editing && (
          <button style={{ ...styles.removeX, color: C.accent }} onClick={start}>{t.edit}</button>
        )}
      </div>
      {editing ? (
        <div style={{ marginTop: 6 }}>
          {multiline
            ? <textarea style={{ ...inputStyle, minHeight: 90 }} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
            : <input style={inputStyle} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />}
          {list && !lines && <p style={{ ...styles.empty, marginTop: 4 }}>{t.commaSeparated}</p>}
          {lines && <p style={{ ...styles.empty, marginTop: 4 }}>{t.onePerLine}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={styles.removeX} onClick={() => setEditing(false)} disabled={saving}>{t.cancel}</button>
            <button style={{ ...styles.removeX, color: C.accent, fontWeight: 600 }} onClick={save} disabled={saving}>{saving ? '…' : t.save}</button>
          </div>
        </div>
      ) : !has ? (
        <p style={styles.empty}>{emptyText}</p>
      ) : lines ? (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.5, listStyle: 'disc' }}>{display.map((x, i) => <li key={i}>{x}</li>)}</ul>
      ) : list ? (
        <div style={styles.chipRow}>{display.map((tr, i) => <span key={i} style={styles.chip}>{tr}</span>)}</div>
      ) : (
        <div style={styles.fieldValue}>{display}</div>
      )}
    </div>
  );
};

// Komplette Zielgruppe bearbeiten (alle Felder) / löschen. Neu: draft ohne Index.
const PERSONA_FIELDS = [
  { key: 'name', multiline: false },
  { key: 'age_range', multiline: false },
  { key: 'context', multiline: true },
  { key: 'pain_point', multiline: true },
  { key: 'purchase_motive', multiline: true },
  { key: 'content_hook', multiline: true },
];

const PersonaCard = ({ persona, isNew, isPrimary, label, onSave, onDelete, onCancelNew, t, lang }) => {
  const [editing, setEditing] = useState(!!isNew);
  const [draft, setDraft] = useState(persona || {});
  const [saving, setSaving] = useState(false);
  const inputStyle = { width: '100%', boxSizing: 'border-box', background: C.page, color: C.text, border: `1px solid ${C.accent}55`, borderRadius: 8, padding: '7px 10px', fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical' };
  const labels = t.personaFields;
  const save = async () => {
    setSaving(true);
    try { await onSave({ ...draft, name: (draft.name || '').trim() }); setEditing(false); }
    finally { setSaving(false); }
  };
  const cancel = () => { if (isNew) onCancelNew(); else { setDraft(persona); setEditing(false); } };

  if (!editing) {
    const lines = [['pain_point', t.pain], ['purchase_motive', t.wants], ['context', labels.context], ['content_hook', labels.content_hook]];
    return (
      <div style={styles.persona}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <p style={styles.personaName}>
            {label}
            {isPrimary && <span style={styles.primaryTag}>{lang === 'de' ? 'PRIMÄR' : 'PRIMARY'}</span>}
          </p>
          <span style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            <button style={{ ...styles.removeX, color: C.accent }} onClick={() => { setDraft(persona); setEditing(true); }}>{t.edit}</button>
            <button style={styles.removeX} onClick={() => { if (window.confirm(t.confirmDeleteAudience)) onDelete(); }}>{t.remove}</button>
          </span>
        </div>
        {persona.age_range ? <p style={styles.personaLine}><span style={styles.personaKey}>{labels.age_range}: </span>{persona.age_range}</p> : null}
        {lines.map(([k, lab]) => nonEmpty(persona[k]) && (
          <p key={k} style={styles.personaLine}><span style={styles.personaKey}>{lab}: </span>{persona[k]}</p>
        ))}
      </div>
    );
  }

  return (
    <div style={styles.persona}>
      <div style={{ marginBottom: 8 }}>
        <p style={styles.fieldLabel}>{t.audienceRole}</p>
        <select style={inputStyle} value={draft.role === 'primary' ? 'primary' : 'secondary'}
          onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
          <option value="primary">{t.rolePrimary}</option>
          <option value="secondary">{t.roleSecondary}</option>
        </select>
      </div>
      {PERSONA_FIELDS.map((f) => (
        <div key={f.key} style={{ marginBottom: 8 }}>
          <p style={styles.fieldLabel}>{labels[f.key]}</p>
          {f.multiline
            ? <textarea style={{ ...inputStyle, minHeight: 60 }} value={draft[f.key] || ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
            : <input style={inputStyle} value={draft[f.key] || ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={styles.removeX} onClick={cancel} disabled={saving}>{t.cancel}</button>
        <button style={{ ...styles.removeX, color: C.accent, fontWeight: 600 }} onClick={save}
          disabled={saving || !(draft.name || '').trim()}>{saving ? '…' : t.save}</button>
      </div>
    </div>
  );
};

const PLATFORM_OPTIONS = [['linkedin', 'LinkedIn'], ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['x', 'X (Twitter)'],
  ['blog', 'Blog'], ['newsletter', 'Newsletter'], ['pinterest', 'Pinterest'], ['medium', 'Medium'], ['youtube', 'YouTube']];
const PLATFORM_LABEL = Object.fromEntries(PLATFORM_OPTIONS);
const PillGroup = ({ options, value, onChange, disabled, testId }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="radiogroup" data-testid={testId}>
    {options.map(([k, label]) => (
      <button type="button" key={k} role="radio" aria-checked={value === k} disabled={disabled} style={styles.pill(value === k)}
        onClick={() => onChange(k)} data-testid={testId ? `${testId}-${k}` : undefined}>{label}</button>
    ))}
  </div>
);

// Plattform und Herkunft als Auswahl-Chips statt Aufklapp-Menü: gleich zuverlässig in jedem Browser und Gerät
const PlatformSelect = ({ value, onChange, disabled, testId }) => (
  <PillGroup
    options={[...PLATFORM_OPTIONS, ...(value && !PLATFORM_LABEL[value] ? [[value, value]] : [])]}
    value={value || ''} onChange={(k) => onChange(k === value ? '' : k)} disabled={disabled} testId={testId} />
);

const OwnSelect = ({ value, onChange, disabled, t, testId }) => (
  <PillGroup
    options={[['own', t.ownOptions.own], ['other', t.ownOptions.other], ['unset', t.ownOptions.unset]]}
    value={value} onChange={onChange} disabled={disabled} testId={testId} />
);

const ownToKey = (v) => (v === true ? 'own' : v === false ? 'other' : 'unset');
const keyToOwn = (k) => (k === 'own' ? true : k === 'other' ? false : null);
const POST_PREVIEW = 240;

// Ein Beispiel-Post: Text aufklappbar, Plattform/Herkunft/Text nachträglich bearbeitbar.
const PostRow = ({ post, isPerson, busy, t, onSave, onRemove, selected, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const text = post.text || '';
  const long = text.length > POST_PREVIEW;

  const startEdit = () => {
    setDraft({ text, platform: post.platform || '', what_i_like: post.what_i_like || '', own: ownToKey(post.is_own) });
    setEditing(true);
  };
  const save = async () => {
    const patch = { text: draft.text.trim(), platform: draft.platform, what_i_like: draft.what_i_like.trim() };
    if (isPerson) patch.is_own = keyToOwn(draft.own);
    if (await onSave(post.id, patch)) setEditing(false);
  };

  return (
    <div style={styles.postRow} data-testid={`insp-${post.id}`}>
      <div style={styles.postHead}>
        <span>
          {post.id && onToggle && (
            <input type="checkbox" checked={!!selected} onChange={() => onToggle(post.id)} disabled={busy} style={{ marginRight: 8, verticalAlign: 'middle' }} aria-label="select" data-testid={`insp-select-${post.id}`} />
          )}
          {nonEmpty(post.platform)
            ? <span style={styles.postPlatform}>{PLATFORM_LABEL[post.platform] || post.platform}</span>
            : <span style={styles.badgeWarn} title={t.noPlatformHint} data-testid={`insp-noplatform-${post.id}`}>{t.noPlatform}</span>}
          {!nonEmpty(post.platform) && nonEmpty(post.suggested_platform) && post.id && (
            <button style={{ ...styles.linkBtn, marginLeft: 8 }} disabled={busy} onClick={() => onSave(post.id, { platform: post.suggested_platform })} data-testid={`insp-suggest-${post.id}`}>
              {t.suggestPlatform(PLATFORM_LABEL[post.suggested_platform] || post.suggested_platform)}
            </button>
          )}
          {isPerson && post.is_own === true && <span style={styles.badgeOwn}>{t.ownBadge}</span>}
          {isPerson && post.is_own === false && <span style={styles.badgeOther}>{t.otherBadge}</span>}
        </span>
        {post.id && !editing && (
          <span>
            <button style={styles.removeX} disabled={busy} onClick={startEdit} data-testid={`insp-edit-${post.id}`}>{t.editPost}</button>
            <button style={{ ...styles.removeX, marginLeft: 10 }} disabled={busy} onClick={() => onRemove(post.id)}>{t.remove}</button>
          </span>
        )}
      </div>

      {editing ? (
        <div style={styles.editBox}>
          <div style={{ ...styles.inlineRow, flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
            <PlatformSelect value={draft.platform} onChange={(v) => setDraft((d) => ({ ...d, platform: v }))} disabled={busy} t={t} testId={`insp-edit-platform-${post.id}`} />
            {isPerson && <OwnSelect value={draft.own} onChange={(v) => setDraft((d) => ({ ...d, own: v }))} disabled={busy} t={t} testId={`insp-edit-own-${post.id}`} />}
          </div>
          <textarea style={styles.editTextarea} value={draft.text} disabled={busy} onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))} data-testid={`insp-edit-text-${post.id}`} />
          <input style={{ ...styles.editInput, width: '100%', boxSizing: 'border-box' }} value={draft.what_i_like} disabled={busy} placeholder={t.whatILike} onChange={(e) => setDraft((d) => ({ ...d, what_i_like: e.target.value }))} />
          <div style={styles.inlineRow}>
            <button style={styles.btnPrimary} disabled={busy || !draft.text.trim()} onClick={save} data-testid={`insp-save-${post.id}`}>{t.save}</button>
            <button style={styles.btn} disabled={busy} onClick={() => setEditing(false)}>{t.cancel}</button>
          </div>
        </div>
      ) : (
        <>
          <p style={styles.postText}>{expanded || !long ? text : `${text.slice(0, POST_PREVIEW)}…`}</p>
          {long && <button style={{ ...styles.linkBtn, marginLeft: 0, marginTop: 6 }} onClick={() => setExpanded((v) => !v)} data-testid={`insp-toggle-${post.id}`}>{expanded ? t.showLess : t.showMore}</button>}
          {nonEmpty(post.what_i_like) && <p style={{ ...styles.hint, marginTop: 6 }}>{post.what_i_like}</p>}
        </>
      )}
    </div>
  );
};

// „So klingst du“: Stimmprofil aus den eigenen Beispiel-Posts (nur Personen-Projekte).
const VoicePanel = ({ lang, refreshKey }) => {
  const { getAuthHeader } = useAuth();
  const T = (de, en) => (lang === 'de' ? de : en);
  const tt = { edit: T('Bearbeiten', 'Edit'), save: T('Speichern', 'Save'), cancel: T('Abbrechen', 'Cancel'),
    commaSeparated: T('Mit Komma trennen', 'Separate with commas'), onePerLine: T('Eine pro Zeile', 'One per line') };
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/active/voice`, { headers: getAuthHeader() });
      setInfo(data);
    } catch (e) { setInfo(null); }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line

  if (!info) return null;
  const voice = info.voice || {};
  const has = !!(voice.summary || voice.tone);
  const min = info.limits?.min_samples || 3;

  const analyze = async () => {
    if (has && !window.confirm(T('Deine Änderungen an der Stimme werden ersetzt. Fortfahren?', 'Your edits to the voice will be replaced. Continue?'))) return;
    setBusy(true); setMsg('');
    try {
      const { data } = await axios.post(`${API}/projects/active/voice/analyze`, {}, { headers: getAuthHeader(), timeout: 120000 });
      setInfo((i) => ({ ...i, voice: data.voice }));
    } catch (e) { setMsg(e?.response?.data?.detail || T('Analyse fehlgeschlagen', 'Analysis failed')); } finally { setBusy(false); }
  };
  const saveField = async (patch) => {
    const next = { ...voice, ...patch };
    const { data } = await axios.put(`${API}/projects/active/personal-brand`, { personal_brand: { voice: next } }, { headers: getAuthHeader() });
    setInfo((i) => ({ ...i, voice: data.personal_brand?.voice || next }));
  };
  const F = (label, key, extra = {}) => (
    <EditableField t={tt} label={label} value={voice[key]} emptyText="—" onSave={(v) => saveField({ [key]: v })} {...extra} />
  );

  const FIELDS = [
    [T('Zusammengefasst', 'Summary'), 'summary', { multiline: true }],
    [T('Ton', 'Tone'), 'tone', {}],
    [T('Sätze & Rhythmus', 'Sentences & rhythm'), 'sentence_style', {}],
    [T('Aufbau', 'Structure'), 'structure', {}],
    [T('Wortwahl', 'Vocabulary'), 'vocabulary', {}],
    [T('Emojis & Hashtags', 'Emoji & hashtags'), 'emoji_hashtags', {}],
    [T('Ansprache', 'Addressing'), 'addressing', {}],
    [T('Typische Einstiege', 'Typical openings'), 'openings', { lines: true }],
    [T('Typische Schlüsse', 'Typical endings'), 'closings', { lines: true }],
    [T('Typische Wendungen', 'Signature phrases'), 'signature_phrases', { lines: true }],
    [T('Das machst du', 'What you do'), 'do', { lines: true }],
    [T('Das machst du nie', 'What you never do'), 'dont', { lines: true }],
  ];
  const filled = (key) => { const v = voice[key]; return Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim()); };
  const emptyCount = FIELDS.filter(([, k]) => !filled(k)).length;

  return (
    <div style={{ ...styles.fieldWrap, paddingBottom: 6 }} data-testid="voice-panel">
      <p style={styles.fieldLabel}>{T('So klingst du', 'How you sound')}</p>
      <p style={styles.hint}>
        {T(`Wird aus deinen eigenen Posts gelernt (unten als „Von mir“ markieren). Aktuell ${info.samples.length}, nötig sind mindestens ${min}.`,
           `Learned from your own posts (mark them “Mine” below). Currently ${info.samples.length}, at least ${min} needed.`)}
      </p>
      <div style={{ ...styles.inlineRow, marginTop: 12, marginBottom: has ? 6 : 0 }}>
        <button style={styles.btnPrimary} disabled={busy || !info.can_analyze} onClick={analyze} data-testid="voice-analyze">
          {busy ? '…' : has ? T('Stimme neu analysieren', 'Re-analyse voice') : T('Stimme analysieren', 'Analyse voice')}
        </button>
        {msg && <span style={styles.warnText}>{msg}</span>}
      </div>
      {has && (
        <div data-testid="voice-result">
          {FIELDS.filter(([, k]) => showEmpty || filled(k)).map(([label, key, extra]) => <span key={key}>{F(label, key, extra)}</span>)}
          {emptyCount > 0 && (
            <button type="button" style={{ ...styles.linkBtn, marginLeft: 0, marginTop: 12 }} onClick={() => setShowEmpty(v => !v)} data-testid="voice-toggle-empty">
              {showEmpty ? T('Leere Felder ausblenden', 'Hide empty fields') : T(`Weitere Felder ergänzen (${emptyCount})`, `Add more fields (${emptyCount})`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nonEmpty = (v) => Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());

export default function BrandDnaView() {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const t = L[lang] || L.de;
  const [loading, setLoading] = useState(true);
  const [dna, setDna] = useState(null);
  const [company, setCompany] = useState(null);
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  // 1b — color suggestions (human-vetted before write)
  const [suggestions, setSuggestions] = useState(null); // null=idle, []=none found
  const [suggestMsg, setSuggestMsg] = useState('');     // reason when no colors
  // Fix 2 — add example post
  const [newPostText, setNewPostText] = useState('');
  const [newPostPlatform, setNewPostPlatform] = useState('');
  const [newPostOwn, setNewPostOwn] = useState('own');
  const [isPerson, setIsPerson] = useState(false);
  const [pTab, setPTab] = useState('du');        // Personal DNA: du | werdegang | stimme | aussehen
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkPlatform, setBulkPlatform] = useState('');
  // Hybrid: full 7-dimension editor, collapsed by default
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Logo overlay (data fields only). style_strength/image_style/language now
  // live in <BrandDnaPreferences/> (single preferences-doc writer).
  const [overlay, setOverlay] = useState({
    overlay_enabled: false, logo_url: null, logo_position: 'bottom-right',
    logo_x_percent: 0.82, logo_y_percent: 0.82, logo_size_percent: 0.15,
  });

  const load = async () => {
    const [dnaRes, inspRes, ovRes] = await Promise.allSettled([
      axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() }),
      axios.get(`${API}/brand/inspirations`, { headers: getAuthHeader() }),
      axios.get(`${API}/settings/overlay`, { headers: getAuthHeader() }),
    ]);
    if (dnaRes.status === 'fulfilled') {
      setDna(dnaRes.value.data?.brandDNA || null);
      setCompany(dnaRes.value.data?.company || null);
    }
    try {
      const pr = await axios.get(`${API}/projects/active`, { headers: getAuthHeader() });
      setIsPerson(pr.data?.project?.project_type === 'person');
    } catch { /* Standard: Unternehmen */ }
    if (inspRes.status === 'fulfilled') {
      const list = inspRes.value.data?.inspirations || [];
      setPosts(list.filter((i) => (i.text || '').trim()));
    }
    if (ovRes.status === 'fulfilled' && ovRes.value.data) {
      setOverlay((prev) => ({ ...prev, ...ovRes.value.data }));
    }
  };

  const saveDnaField = async (fieldPath, value) => {
    try {
      await axios.put(`${API}/settings/brand-dna/field`, { field_path: fieldPath, value },
        { headers: getAuthHeader() });
      setDna((prev) => {
        const next = { ...(prev || {}) };
        const keys = fieldPath.split('.');
        let cur = next;
        keys.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
        cur[keys[keys.length - 1]] = value;
        return next;
      });
      toast.success(t.saved);
    } catch (e) {
      toast.error(t.saveFailed);
      throw e;
    }
  };

  const saveIndustry = async (value) => {
    try {
      await axios.put(`${API}/settings/brand-dna/company`, { industry: value },
        { headers: getAuthHeader() });
      setCompany((prev) => ({ ...(prev || {}), industry: value }));
      toast.success(t.saved);
    } catch (e) {
      toast.error(t.saveFailed);
      throw e;
    }
  };

  const [addingAudience, setAddingAudience] = useState(false);
  const allPersonas = () => (Array.isArray(dna?.personas) ? dna.personas.map((p) => ({ ...p })) : []);
  const savePersona = async (index, value) => {
    let all = allPersonas();
    if (index === null) all.push(value); else all[index] = value;
    // Genau eine primäre Zielgruppe: wird diese primär, werden andere sekundär
    if (value.role === 'primary') all = all.map((p, i) => (i === (index === null ? all.length - 1 : index) ? p : { ...p, role: 'secondary' }));
    await saveDnaField('personas', all);
    if (index === null) setAddingAudience(false);
  };
  const deletePersona = async (index) => {
    await saveDnaField('personas', allPersonas().filter((_, i) => i !== index));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try { if (alive) await load(); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [getAuthHeader]);

  // ── 1b: fetch color SUGGESTIONS (dry-run, writes nothing) ──
  // Fix 3: distinguish "no website URL on file" (422) from "URL present but no
  // colors found" (SPA/empty CSS) so the message is diagnostic, not misleading.
  const suggestColors = async () => {
    setBusy(true);
    setSuggestMsg('');
    try {
      const r = await axios.post(`${API}/settings/brand-dna/extract-colors`, { dry_run: true },
        { headers: getAuthHeader() });
      const found = (r.data?.extracted_colors || []).map((c) => ({ ...c, include: true }));
      setSuggestions(found);
      if (found.length === 0) {
        setSuggestMsg(t.noColorsForUrl(r.data?.url || ''));
      }
    } catch (e) {
      setSuggestions([]);
      setSuggestMsg(e?.response?.status === 422 ? t.noUrl : (e?.response?.data?.detail || t.noSuggestions));
    } finally { setBusy(false); }
  };

  // ── Fix 2: add example post via EXISTING endpoints (no new write path) ──
  const addPostText = async () => {
    const text = newPostText.trim();
    if (!text || !newPostPlatform) return;
    setBusy(true);
    try {
      await axios.post(`${API}/brand/inspirations`,
        { type: 'post', text, platform: newPostPlatform, ...(isPerson ? { is_own: keyToOwn(newPostOwn) } : {}) },
        { headers: getAuthHeader() });
      setNewPostText(''); setNewPostPlatform('');
      await load();
    } catch (e) { /* no-op */ } finally { setBusy(false); }
  };

  const toggleSelected = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const bulkApply = async (patch) => {
    if (!selected.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/brand/inspirations/bulk`, { ids: selected, ...patch }, { headers: getAuthHeader() });
      setSelected([]); setBulkPlatform('');
      await load();
    } catch (e) { window.alert(e?.response?.data?.detail || t.bulkFailed); } finally { setBusy(false); }
  };

  const savePost = async (id, patch) => {
    setBusy(true);
    try {
      await axios.put(`${API}/brand/inspirations/${id}`, patch, { headers: getAuthHeader() });
      await load();
      return true;
    } catch (e) { return false; } finally { setBusy(false); }
  };

  const removePost = async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/brand/inspirations/${id}`, { headers: getAuthHeader() });
      await load();
    } catch (e) { /* no-op */ } finally { setBusy(false); }
  };

  // ── Ported: logo overlay → POST /settings/overlay (data only) ──
  const saveOverlay = async (patch) => {
    const next = { ...overlay, ...patch };
    setOverlay(next); // optimistic
    try {
      await axios.post(`${API}/settings/overlay`, next,
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } });
    } catch (e) { /* keep optimistic value */ }
  };
  const uploadOverlayLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/settings/overlay/logo`, fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setOverlay((prev) => ({ ...prev, logo_url: r.data?.logo_url || prev.logo_url }));
    } catch (e) { /* no-op */ } finally { setBusy(false); }
  };

  // ── 1b: write the HUMAN-CURATED selection ──
  const applyColors = async () => {
    const chosen = (suggestions || []).filter((c) => c.include)
      .map((c) => ({ hex: c.hex, role: c.role, usage: c.usage || 'from website (reviewed)' }));
    if (!chosen.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/settings/brand-dna/extract-colors`, { dry_run: false, colors: chosen },
        { headers: getAuthHeader() });
      setSuggestions(null);
      await load();
    } catch (e) { /* leave suggestions visible */ } finally { setBusy(false); }
  };

  // ── 1c: uploads (logo / reference images) → data fields only ──
  const uploadImage = async (path, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/settings/brand-dna/${path}`, fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (e) { /* no-op */ } finally { setBusy(false); }
  };

  const removeLogo = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/settings/brand-dna/logo`, { headers: getAuthHeader() });
      await load();
    } catch (e) { /* no-op */ } finally { setBusy(false); }
  };

  // Personal DNA: ein gemeinsamer Stand für Übersicht, Tabs und Speichern-Leiste (nur bei Personen aktiv)
  const dnaState = usePersonalDna({ enabled: isPerson, lang, onChanged: load });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.wrap}><p style={styles.empty}>{t.loading}</p></div>
      </div>
    );
  }

  const identity  = dna?.brand_identity || {};
  const voice     = dna?.voice_and_tone || {};
  const visual    = dna?.visual_identity || {};
  const personas  = Array.isArray(dna?.personas) ? dna.personas : [];
  const traits    = Array.isArray(voice.personality_traits) ? voice.personality_traits : [];
  const colors    = Array.isArray(visual.colors) ? visual.colors : [];
  const logoUrl   = visual.logo_url || '';

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        <h1 style={styles.h1}>{isPerson ? t.pageTitleP : t.pageTitle}</h1>
        <p style={styles.pageSub}>{isPerson ? t.pageSubP : t.pageSub}</p>

        {/* ── Chat editor (v1: positioning / usp / traits) und Brand Manual: nur für Firmen.
            Bei Personen wird die DNA aus dem Profil abgeleitet. ───────── */}
        {!isPerson && (
          <>
            <div style={{ marginBottom: 20 }}>
              <BrandDnaChat onChanged={load} />
            </div>
            <section style={styles.card}>
              <BlockHead title={lang === 'de' ? 'Brand Manual hochladen' : 'Upload brand manual'} />
              <BrandManualImport getAuthHeader={getAuthHeader} lang={lang} onApplied={load} />
            </section>
          </>
        )}

        {/* ── Personal DNA: Übersicht, dann vier Bereiche als Tabs ── */}
        {isPerson && (
          <div data-testid="personal-dna-block">
            <PersonalDnaOverview s={dnaState} hasVisuals={colors.length > 0 || !!logoUrl} onGo={setPTab} lang={lang} />
            <DnaNotices s={dnaState} lang={lang} />

            <div style={{ ...styles.segRow, marginTop: 0, marginBottom: 18 }} role="tablist" data-testid="dna-tabs">
              {[['du', lang === 'de' ? 'Du' : 'You'], ['werdegang', lang === 'de' ? 'Werdegang & Firmen' : 'Career & companies'],
                ['stimme', lang === 'de' ? 'Stimme' : 'Voice'], ['aussehen', lang === 'de' ? 'Aussehen' : 'Look']].map(([id, label]) => (
                <button key={id} role="tab" aria-selected={pTab === id} style={styles.seg(pTab === id)} onClick={() => setPTab(id)} data-testid={`dna-tab-${id}`}>
                  {label}{dnaState.dirty && (id === 'du' || id === 'werdegang') ? ' •' : ''}
                </button>
              ))}
            </div>

            {/* Profil-Tabs bleiben eingehängt (nur ausgeblendet), damit ungespeicherte Änderungen beim Wechseln nicht verloren gehen */}
            <div style={{ display: pTab === 'du' || pTab === 'werdegang' ? 'block' : 'none' }}>
              <DnaSaveBar s={dnaState} lang={lang} />
              {dnaState.profile && (
                <>
                  <div style={{ display: pTab === 'du' ? 'block' : 'none', marginBottom: 20 }}>
                    <DnaProfileTab profile={dnaState.profile} onChange={dnaState.update} lang={lang} />
                  </div>
                  <div style={{ display: pTab === 'werdegang' ? 'block' : 'none', marginBottom: 20 }}>
                    <DnaCareerTab profile={dnaState.profile} onChange={dnaState.update} lang={lang} onAddRoleWebsite={dnaState.addRoleWebsite}
                      cvSlot={<PersonalCvUpdate lang={lang} sources={dnaState.cvSources} onChanged={() => { dnaState.reload(); load(); }} />} />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── BLOCK 1: Who you are ─────────────────────────────────── */}
        {!isPerson && (
        <section style={styles.card}>
          <BlockHead title={t.who} />

          <EditableField t={t} multiline label={t.positioning} value={identity.positioning} emptyText={t.empty}
            onSave={(v) => saveDnaField('brand_identity.positioning', v)} />
          <EditableField t={t} multiline label={t.productPromise} value={identity.product_promise} emptyText={t.empty}
            onSave={(v) => saveDnaField('brand_identity.product_promise', v)} />
          <EditableField t={t} multiline label={t.usp} value={identity.usp} emptyText={t.empty}
            onSave={(v) => saveDnaField('brand_identity.usp', v)} />
          <EditableField t={t} multiline label={t.marketPosition} value={identity.market_position} emptyText={t.empty}
            onSave={(v) => saveDnaField('brand_identity.market_position', v)} />
          <EditableField t={t} label={t.industry} value={company?.industry} emptyText={t.empty}
            onSave={saveIndustry} />
          <EditableField t={t} multiline lines label={t.extraGuidelines} value={dna?.additional_guidelines} emptyText={t.empty}
            onSave={(v) => saveDnaField('additional_guidelines', v)} />

          <div style={styles.fieldWrap}>
            <p style={styles.fieldLabel}>{t.audiences}</p>
            {personas.length === 0 && !addingAudience && <p style={styles.empty}>{t.empty}</p>}
            <div style={styles.personaGrid}>
              {personas.map((p, i) => {
                const isPrimary = p?.role === 'primary' || (i === 0 && !personas.some((x) => x?.role === 'primary'));
                return (
                  <PersonaCard key={`${i}-${p?.name || ''}`} persona={p} isPrimary={isPrimary} t={t} lang={lang}
                    label={audienceLabel(p, `${t.audiences} ${i + 1}`)}
                    onSave={(v) => savePersona(i, v)} onDelete={() => deletePersona(i)} />
                );
              })}
              {addingAudience && (
                <PersonaCard isNew persona={{ role: personas.length ? 'secondary' : 'primary' }} t={t} lang={lang}
                  onSave={(v) => savePersona(null, v)} onCancelNew={() => setAddingAudience(false)} />
              )}
            </div>
            {!addingAudience && (
              <button style={{ ...styles.removeX, color: C.accent, marginTop: 10 }} onClick={() => setAddingAudience(true)}>{t.addAudience}</button>
            )}
          </div>
        </section>
        )}

        {/* ── BLOCK 2: How you sound ───────────────────────────────── */}
        {(!isPerson || pTab === 'stimme') && (
        <section style={styles.card}>
          <BlockHead title={isPerson ? t.soundP : t.sound} />

          {!isPerson && (
            <EditableField t={t} list label={t.character} value={traits} emptyText={t.empty}
              onSave={(v) => saveDnaField('voice_and_tone.personality_traits', v)} />
          )}

          {isPerson && <VoicePanel lang={lang} refreshKey={posts.map((p) => `${p.id}:${p.is_own}:${p.platform}`).join('|')} />}

          <div style={styles.fieldWrap}>
            <p style={styles.fieldLabel}>{t.examplePosts}</p>
            {posts.length === 0 ? (
              <p style={styles.empty}>{t.empty}</p>
            ) : (
              <>
                <p style={styles.countPill}>{t.posts(posts.length)}</p>
                {posts.filter((p) => !nonEmpty(p.platform)).length > 0 && (
                  <p style={styles.warnText} data-testid="insp-missing-platform">{t.missingPlatformHint(posts.filter((p) => !nonEmpty(p.platform)).length)}</p>
                )}
                {posts.some((p) => !nonEmpty(p.platform)) && (
                  <button style={{ ...styles.linkBtn, marginLeft: 0, marginTop: 8 }} onClick={() => setSelected(posts.filter((p) => p.id && !nonEmpty(p.platform)).map((p) => p.id))} data-testid="insp-select-noplatform">{t.selectNoPlatform}</button>
                )}
                {selected.length > 0 && (
                  <div style={{ ...styles.inlineRow, marginTop: 10 }} data-testid="insp-bulk-bar">
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{t.selectedN(selected.length)}</span>
                    <PlatformSelect value={bulkPlatform} onChange={setBulkPlatform} disabled={busy} t={t} testId="insp-bulk-platform" />
                    <button style={styles.btnPrimary} disabled={busy || !bulkPlatform} onClick={() => bulkApply({ platform: bulkPlatform })} data-testid="insp-bulk-apply">{t.applyPlatform}</button>
                    {isPerson && <button style={styles.btn} disabled={busy} onClick={() => bulkApply({ is_own: true })} data-testid="insp-bulk-own">{t.markOwn}</button>}
                    {isPerson && <button style={styles.btn} disabled={busy} onClick={() => bulkApply({ is_own: false })} data-testid="insp-bulk-other">{t.markOther}</button>}
                    <button style={styles.linkBtn} onClick={() => setSelected([])}>{t.clearSel}</button>
                  </div>
                )}
                {(showAllPosts ? posts : posts.slice(0, 8)).map((p, i) => (
                  <PostRow key={p.id || i} post={p} isPerson={isPerson} busy={busy} t={t} onSave={savePost} onRemove={removePost}
                    selected={selected.includes(p.id)} onToggle={toggleSelected} />
                ))}
                {posts.length > 8 && (
                  <button style={{ ...styles.linkBtn, marginLeft: 0, marginTop: 10 }} onClick={() => setShowAllPosts((v) => !v)} data-testid="insp-show-all">
                    {showAllPosts ? t.showFewer : t.showAll(posts.length)}
                  </button>
                )}
              </>
            )}

            {/* Fix 2 — add example post (paste text or upload screenshot) */}
            <div style={styles.addPostBox}>
              <div style={{ ...styles.inlineRow, flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
                <PlatformSelect value={newPostPlatform} onChange={setNewPostPlatform} disabled={busy} t={t} testId="insp-new-platform" />
                {isPerson && <OwnSelect value={newPostOwn} onChange={setNewPostOwn} disabled={busy} t={t} testId="insp-new-own" />}
              </div>
              {isPerson && <p style={{ ...styles.hint, margin: 0 }}>{t.ownHint}</p>}
              <textarea style={styles.postTextarea} value={newPostText} disabled={busy}
                placeholder={t.pastePost}
                onChange={(e) => setNewPostText(e.target.value)} />
              <div style={styles.inlineRow}>
                <button style={styles.btnPrimary} disabled={busy || !newPostText.trim() || !newPostPlatform} onClick={addPostText} data-testid="insp-add">
                  {t.addPost}
                </button>
              </div>
            </div>
          </div>
        </section>

        )}

        {/* ── BLOCK 3: How you look ────────────────────────────────── */}
        {(!isPerson || pTab === 'aussehen') && (
        <>
        <section style={styles.card}>
          <BlockHead title={isPerson ? t.lookP : t.look} />

          <div style={styles.fieldWrap}>
            <p style={styles.fieldLabel}>{t.colors}</p>
            {colors.length === 0 ? (
              <p style={styles.empty}>{t.empty}</p>
            ) : (
              <div style={styles.swatchRow}>
                {colors.map((c, i) => {
                  const hex = (c?.hex || '').trim();
                  const valid = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex);
                  return (
                    <div key={i} style={styles.swatch}>
                      <div style={{ ...styles.swatchBox, background: valid ? hex : C.page }} />
                      <p style={styles.swatchHex}>{hex || '—'}</p>
                      {nonEmpty(c?.role) && <p style={styles.swatchRole}>{c.role}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 1b — color SUGGESTION tool (human vets before write; often inaccurate) */}
            {suggestions === null ? (
              <button style={styles.uploadLabel} disabled={busy} onClick={suggestColors}>
                {busy ? t.uploading : t.suggestColors}
              </button>
            ) : suggestions.length === 0 ? (
              <div style={styles.inlineRow}>
                <span style={styles.empty}>{suggestMsg || t.noSuggestions}</span>
                <button style={styles.linkBtn} onClick={() => { setSuggestions(null); setSuggestMsg(''); }}>{t.discard}</button>
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                <p style={styles.hint}>{t.suggestHint}</p>
                {suggestions.map((c, i) => (
                  <div key={i} style={styles.sugRow}>
                    <input type="checkbox" checked={c.include} disabled={busy}
                      onChange={(e) => setSuggestions((s) => s.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))} />
                    <div style={{ ...styles.swatchBox, width: 40, height: 24 }} />
                    <span style={styles.swatchHex}>{c.hex}</span>
                    <select style={styles.roleSelect} value={c.role || 'primary'} disabled={busy}
                      onChange={(e) => setSuggestions((s) => s.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                ))}
                <div style={styles.inlineRow}>
                  <button style={styles.btnPrimary} disabled={busy} onClick={applyColors}>{t.apply}</button>
                  <button style={styles.btn} disabled={busy} onClick={() => setSuggestions(null)}>{t.discard}</button>
                </div>
              </div>
            )}
          </div>

          <div style={styles.fieldWrap}>
            <p style={styles.fieldLabel}>{t.logo}</p>
            <div style={styles.logoBox}>
              {nonEmpty(logoUrl)
                ? <img src={logoUrl} alt="Brand logo" style={styles.logoImg} />
                : <span style={styles.logoPlaceholder}>{t.empty}</span>}
            </div>
            <div style={styles.inlineRow}>
              <label style={styles.uploadLabel}>
                {busy ? t.uploading : t.uploadLogo}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy}
                  onChange={(e) => uploadImage('logo', e.target.files?.[0])} />
              </label>
              {nonEmpty(logoUrl) && (
                <button style={styles.linkBtn} disabled={busy} onClick={removeLogo}>{t.remove}</button>
              )}
            </div>
          </div>

          {/* Visual references moved to the shared <BrandMediaManager/> below
              (writes brand_media, which the generation actually reads). */}

          {/* Ported — logo overlay (POST /settings/overlay + /settings/overlay/logo) */}
          <div style={styles.fieldWrap}>
            <p style={styles.fieldLabel}>{t.logoOverlay}</p>

            <div style={styles.toggleRow}>
              <div style={styles.toggle(overlay.overlay_enabled)}
                onClick={() => saveOverlay({ overlay_enabled: !overlay.overlay_enabled })}>
                <div style={styles.toggleKnob(overlay.overlay_enabled)} />
              </div>
              <span style={{ fontSize: 13 }}>{t.overlayOn}</span>
            </div>

            <div style={styles.inlineRow}>
              {nonEmpty(overlay.logo_url)
                ? <img src={overlay.logo_url} alt="Overlay logo" style={{ height: 40, objectFit: 'contain', border: `0.5px solid ${C.border}`, borderRadius: 6, padding: 4 }} />
                : <span style={styles.empty}>{t.overlayNoLogo}</span>}
              <label style={styles.uploadLabel}>
                {busy ? t.uploading : t.overlayLogoUpload}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy}
                  onChange={(e) => uploadOverlayLogo(e.target.files?.[0])} />
              </label>
            </div>

            {/* Real drag/resize placement editor (shared with Settings.js) —
                writes logo_x/y_percent + logo_size_percent via saveOverlay. */}
            <div style={{ marginTop: 12, maxWidth: 340 }}>
              <LogoPlacementEditor
                overlaySettings={overlay}
                onSave={(placement) => saveOverlay(placement)}
              />
            </div>
          </div>
        </section>

        {/* Visual references — shared component; writes to brand_media (read by generation) */}
        <div style={{ marginBottom: 20 }}>
          <BrandMediaManager getAuthHeader={getAuthHeader} />
        </div>

        {/* Preferences — language + image style + fidelity (single preferences-doc writer) */}
        <div style={{ marginBottom: 20 }}>
          <BrandDnaPreferences getAuthHeader={getAuthHeader} />
        </div>
        </>
        )}

        {/* Advanced — nur Firmen. Bei Personen kommt die DNA aus dem Profil, ihre Optik steht oben bei „Wie du aussiehst“. */}
        {!isPerson && (
        <section style={styles.card}>
          <button
            style={styles.advancedToggle}
            onClick={() => setShowAdvanced((s) => !s)}
          >
            <span>
              <span style={styles.blockTitle}>{t.advanced}</span>
              <span style={styles.advancedHint}> · {t.advancedHint}</span>
            </span>
            <span style={{ color: C.accent, fontSize: 18 }}>{showAdvanced ? '−' : '+'}</span>
          </button>
          {showAdvanced && (
            <div style={{ marginTop: 14 }}>
              <BrandDNAUebersicht getAuthHeader={getAuthHeader} />
              {/* Company facts — moved from old "Visuell" tab (company_profiles, no usps) */}
              <div style={{ marginTop: 16 }}>
                <BrandCompanyEditor getAuthHeader={getAuthHeader} />
              </div>
              {/* Legacy visual colors/mood — moved from old "Visuell" tab (data-only) */}
              <div style={{ marginTop: 16 }}>
                <BrandLegacyVisualEditor getAuthHeader={getAuthHeader} />
              </div>
              {/* SEO & GEO — moved verbatim from the old "Erweitert" tab (same endpoints) */}
              <div style={{ marginTop: 16 }}>
                <BrandDnaAdvancedSettings getAuthHeader={getAuthHeader} />
              </div>
              {/* Re-Analyze from screenshots — moved from the old "Inspirationen" tab */}
              <div style={{ marginTop: 16 }}>
                <BrandDnaReanalyze getAuthHeader={getAuthHeader} />
              </div>
            </div>
          )}
        </section>
        )}
      </div>
    </div>
  );
}
